import { createFileRoute } from '@tanstack/react-router';
import { useGetFeedSpaces, useGetSpace, useGetSubSpaces } from '@onesource/services';
import { SpaceType } from '@onesource/schemas';
import { CommunityTabContent } from '../../../../../features/community-hub/components/community-tab-content';
import { FeedTabContent } from '../../../../../features/community-hub/components/feed-tab-content';
import { SpaceNoAccessPage } from '../../../../../features/community-hub/components/space-no-access-page';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

/**
 * Space Community Route
 * Path: /community-hub/spaces/:id/community
 *
 * Handles three cases:
 * 1. If user has no access -> shows space name + banner with lock overlay
 * 2. If :id is a CONTAINER space -> shows sub-tabs for each FEED sub-space
 * 3. If :id is a FEED space -> shows posts directly from that feed
 */
export const Route = createFileRoute('/_protected/community-hub/spaces/$id/community')({
  component: SpaceCommunityRoute,
});

function SpaceCommunityRoute() {
  const { id } = Route.useParams();
  const { data: space, isLoading: isLoadingSpace, error: spaceError } = useGetSpace(id);
  const { data: feedSpaces } = useGetFeedSpaces();

  const noAccessibleFeedSpace = feedSpaces?.find(space => space.id === id);

  // Only fetch sub-spaces if this is a container space with full access (has id)
  const hasAccess = noAccessibleFeedSpace?.hasAccess;
  const isContainer = space?.spaceType === SpaceType.CONTAINER;
  const { data: subSpaces, isLoading: isLoadingSubSpaces } = useGetSubSpaces(id, {
    enabled: hasAccess && isContainer,
  });

  if (isLoadingSpace || (hasAccess && isContainer && isLoadingSubSpaces)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (spaceError) {
    return <Alert severity="error">Failed to load space: {spaceError.message}</Alert>;
  }

  if (!space) {
    return <Alert severity="error">Space not found</Alert>;
  }

  // No access - show locked page with space name and banner
  if (!hasAccess && noAccessibleFeedSpace) {
    return <SpaceNoAccessPage name={noAccessibleFeedSpace.name} bannerImage={noAccessibleFeedSpace.bannerImage} />;
  }

  // If this is a FEED space, show posts directly
  if (space.spaceType === SpaceType.FEED) {
    // parentSpace is unused in FeedTabContent, so we pass the space itself
    return <FeedTabContent subSpace={space} parentSpace={space} />;
  }

  // If this is a CONTAINER space, show sub-tabs for each FEED sub-space
  const feedSubSpaces = subSpaces?.filter(s => s.spaceType === SpaceType.FEED) || [];

  return <CommunityTabContent subSpaces={feedSubSpaces} parentSpace={space} />;
}
